import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getAuthLogs } from '../../../lib/api';
import { isAdminSession } from '../../../lib/adminEmail';
import '../../report/pages/summary.css';
import '../../report/pages/detailsReport.css';
import './auth-logs.css';

const PAGE_SIZE = 20;

const iso = (d) => d.toISOString().slice(0, 10);

const actionLabel = (action) => {
  if (action === 'login') return 'Login';
  if (action === 'logout') return 'Logout';
  if (action === 'login_failed') return 'Login failed';
  return action || '—';
};

const actionClass = (action) => {
  if (action === 'login') return 'auth-log-badge auth-log-badge--login';
  if (action === 'logout') return 'auth-log-badge auth-log-badge--logout';
  if (action === 'login_failed') return 'auth-log-badge auth-log-badge--failed';
  return 'auth-log-badge';
};

const fmtDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
};

const AuthLogs = () => {
  const today = useMemo(() => new Date(), []);
  const last30 = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d;
  }, []);

  const [fromDate, setFromDate] = useState(() => iso(last30));
  const [toDate, setToDate] = useState(() => iso(today));
  const [action, setAction] = useState('all');
  const [email, setEmail] = useState('');
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const isAdmin = isAdminSession();

  const loadLogs = useCallback(async (nextPage = 1) => {
    setLoading(true);
    setError('');
    try {
      const payload = {
        page: nextPage,
        limit: PAGE_SIZE,
        from_date: fromDate || undefined,
        to_date: toDate || undefined,
        action,
      };
      if (isAdmin && email.trim()) {
        payload.email = email.trim();
      }
      const data = await getAuthLogs(payload);
      setRows(data?.data || []);
      setTotalPages(data?.pagination?.total_pages || 1);
      setPage(nextPage);
    } catch (e) {
      setError(e?.message || 'Failed to load auth logs');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, action, email, isAdmin]);

  useEffect(() => {
    loadLogs(1);
  }, []);

  const onSearch = () => loadLogs(1);

  return (
    <div className="summary-wrap auth-logs-page">
      <div className="summary-header">
        <h2>Login &amp; Logout Logs</h2>
        <div className="summary-meta">
          {loading ? 'Loading…' : `${rows.length} on this page`}
        </div>
      </div>

      <div className="filter-bar filter-bar--auth-logs">
        <div className="field">
          <label htmlFor="auth-logs-from">From</label>
          <input
            id="auth-logs-from"
            className="filt-control"
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="auth-logs-to">To</label>
          <input
            id="auth-logs-to"
            className="filt-control"
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="auth-logs-action">Action</label>
          <select
            id="auth-logs-action"
            className="filt-control"
            value={action}
            onChange={(e) => setAction(e.target.value)}
          >
            <option value="all">All</option>
            <option value="login">Login</option>
            <option value="logout">Logout</option>
            <option value="login_failed">Login failed</option>
          </select>
        </div>
        {isAdmin && (
          <div className="field">
            <label htmlFor="auth-logs-email">Email</label>
            <input
              id="auth-logs-email"
              className="filt-control"
              type="text"
              placeholder="Filter by email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
        )}
        <div className="actions filter-bar-actions">
          <button type="button" className="btn btn-primary" onClick={onSearch} disabled={loading}>
            {loading ? 'Searching…' : 'Search'}
          </button>
        </div>
      </div>

      {error && <div className="error" style={{ marginBottom: 8 }}>{error}</div>}

      <div className="summary-scroll auth-logs-scroll">
        <div className="summary-table-wrap">
          <table className="summary-table">
            <thead>
              <tr>
                <th>Date &amp; time</th>
                <th>User</th>
                <th>Email</th>
                <th>Action</th>
                <th>IP address</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={`sk-${i}`} className="skeleton-row">
                    {Array.from({ length: 6 }).map((__, j) => (
                      <td key={j}><div className={`sk sk-text ${j === 3 ? 'sk-pill' : ''}`} /></td>
                    ))}
                  </tr>
                ))
              ) : rows.length > 0 ? (
                rows.map((row) => (
                  <tr key={row.id}>
                    <td data-label="Date & time">{fmtDateTime(row.created_at)}</td>
                    <td data-label="User">{row.user_name || '—'}</td>
                    <td data-label="Email">{row.email || '—'}</td>
                    <td data-label="Action">
                      <span className={actionClass(row.action)}>{actionLabel(row.action)}</span>
                    </td>
                    <td data-label="IP address">{row.ip_address || '—'}</td>
                    <td data-label="Status">
                      {row.success ? (
                        <span className="auth-log-badge auth-log-badge--success">Success</span>
                      ) : (
                        <span className="auth-log-badge auth-log-badge--failed">
                          {row.failure_reason || 'Failed'}
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="empty-cell">No login or logout logs found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="pagination-controls">
            <button
              type="button"
              disabled={page <= 1 || loading}
              onClick={() => loadLogs(page - 1)}
            >
              Previous
            </button>
            <span>Page {page} of {totalPages}</span>
            <button
              type="button"
              disabled={page >= totalPages || loading}
              onClick={() => loadLogs(page + 1)}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthLogs;
